import React, { useState, useEffect, useRef } from 'react';
import useModalBackButton from '../../hooks/useModalBackButton';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  ChevronDown,
  X,
  Eye,
  EyeOff,
  Check,
  Calendar,
  Clock,
  AlertTriangle,
  BriefcaseBusiness,
  Users,
  TrendingUp,
  Gift,
  ArrowLeft,
  CalendarRange,
  History
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/Management.css';
import * as discountsApi from '../../services/discountsApi';
import * as servicesApi from '../../services/servicesApi';
import DatePicker from '../../components/DatePicker';
import FloatingField from '../../components/FloatingField';
import { ConfirmModal } from '../../components/ConfirmModal';
import useToast from '../../hooks/useToast';
import { haptic } from '../../utils/vibration';
import DiscountActivityModal from '../../components/DiscountActivityModal';
import ModalShell from '../../components/ModalShell';

// Статусы акций
const PROMOTION_STATUS_LABELS = {
  draft: 'Черновик',
  active: 'Активна',
  scheduled: 'Запланирована',
  expired: 'Истекла',
  archived: 'В архиве'
};

const MANAGEMENT_DATE_PICKER_INPUT_CLASS = 'h-[42px] px-3 pl-8 pr-8 text-sm placeholder:text-sm';
const DESCRIPTION_MAX_LENGTH = 300;
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Пн' },
  { value: 1, label: 'Вт' },
  { value: 2, label: 'Ср' },
  { value: 3, label: 'Чт' },
  { value: 4, label: 'Пт' },
  { value: 5, label: 'Сб' },
  { value: 6, label: 'Вс' }
];
const SERVICE_TYPE_OPTIONS = [
  { value: 'washing', label: 'Стирка' },
  { value: 'cleaning', label: 'Чистка' },
  { value: 'extra_washing', label: 'Доп. стирка' },
  { value: 'extra_cleaning', label: 'Доп. чистка' },
  { value: 'extra_order', label: 'Доп. услуги заказа' }
];
const PROMOTION_SCOPE_OPTIONS = [
  { value: 'category', label: 'На тип услуги' },
  { value: 'group', label: 'На группу' },
  { value: 'service', label: 'На услугу' }
];

const TERMINAL_PROMOTION_STATUSES = new Set(['draft', 'archived', 'expired']);

const normalizePromotionStatus = (value) => String(value || '').trim().toLowerCase();

const parsePromotionDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const [year, month, day] = raw.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const startOfToday = (value = new Date()) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const isSameCalendarDate = (left, right) => (
  left instanceof Date
  && right instanceof Date
  && left.getFullYear() === right.getFullYear()
  && left.getMonth() === right.getMonth()
  && left.getDate() === right.getDate()
);

const parsePromotionTimeMinutes = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const [hours, minutes] = raw.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const toPromotionWeekday = (date) => {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
};

const hasFuturePromotionActivation = (item, referenceDate = new Date()) => {
  const today = startOfToday(referenceDate);
  const dateFrom = parsePromotionDate(item?.date_from);
  const dateTo = parsePromotionDate(item?.date_to);

  if (dateTo && dateTo < today) return false;
  if (dateFrom && dateFrom > today) return true;

  const weekdays = Array.isArray(item?.days_of_week)
    ? item.days_of_week.map(Number).filter(Number.isFinite)
    : [];
  const startMinutes = parsePromotionTimeMinutes(item?.time_from);
  const endMinutes = parsePromotionTimeMinutes(item?.time_to);
  const currentMinutes = referenceDate.getHours() * 60 + referenceDate.getMinutes();
  const lookaheadLimit = new Date(today);
  lookaheadLimit.setDate(lookaheadLimit.getDate() + 7);
  const lastDate = dateTo && dateTo < lookaheadLimit ? dateTo : lookaheadLimit;

  for (let cursor = new Date(today); cursor <= lastDate; cursor.setDate(cursor.getDate() + 1)) {
    if (dateFrom && cursor < dateFrom) continue;
    if (weekdays.length > 0 && !weekdays.includes(toPromotionWeekday(cursor))) continue;

    if (isSameCalendarDate(cursor, today)) {
      if (startMinutes === null || endMinutes === null) continue;
      if (currentMinutes < startMinutes) return true;
      continue;
    }

    return true;
  }

  return false;
};

const getPromotionDisplayStatus = (item, referenceDate = new Date()) => {
  const storedStatus = normalizePromotionStatus(item?.status);
  if (TERMINAL_PROMOTION_STATUSES.has(storedStatus)) return storedStatus;

  const today = startOfToday(referenceDate);
  const dateFrom = parsePromotionDate(item?.date_from);
  const dateTo = parsePromotionDate(item?.date_to);

  if (dateTo && dateTo < today) return 'expired';
  if (dateFrom && dateFrom > today) return 'scheduled';
  if (item?.is_active_now) return 'active';

  return hasFuturePromotionActivation(item, referenceDate) ? 'scheduled' : 'expired';
};

const normalizeDateFieldValue = (value) => (
  value ? String(value).split('T')[0] : ''
);

const isPromotionArchivedLike = (item) => (
  ['archived', 'expired'].includes(getPromotionDisplayStatus(item))
);

const clampDiscountValue = (value, discountType) => {
  if (value === '') return '';
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return '';
  if (discountType === 'percent') {
    return Math.min(Math.max(parsedValue, 0), 100);
  }
  return parsedValue;
};

const getDiscountValueInputMax = (discountType) => (
  discountType === 'percent' ? '100' : undefined
);

const isDiscountPercentLimitExceeded = (discountType, discountValue) => (
  discountType === 'percent' && Number(discountValue || 0) > 100
);

const normalizePromotionPayload = (form) => ({
  ...form,
  discount_value: Number(form.discount_value),
  date_to: form.date_to || null,
  time_from: form.time_from || null,
  time_to: form.time_to || null,
  service_id: form.service_id || null,
  group_id: form.group_id || null,
  min_order_amount: form.min_order_amount ?? null,
  min_items_count: form.min_items_count ?? null,
  every_nth_item_free: form.every_nth_item_free ?? null,
  banner_color: form.banner_color || null,
});

const normalizeNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getScheduledDiscountDraftSource = (item) => {
  const plannedChange = item?.planned_change || null;
  const isWholesaleSystemDiscount = item?.system_code === 'wholesale_order';

  return {
    plannedChange,
    discount_type: plannedChange?.discount_type || item?.discount_type,
    discount_value: plannedChange?.discount_value ?? item?.discount_value,
    min_items_count: isWholesaleSystemDiscount
      ? (item?.min_items_count ?? null)
      : (plannedChange?.min_items_count ?? item?.min_items_count ?? null),
    effective_from: plannedChange?.effective_from
      ? new Date(plannedChange.effective_from).toISOString().split('T')[0]
      : new Date(Date.now() + 86400000).toISOString().split('T')[0],
  };
};

const hasScheduledDiscountVersioningChanges = (form, item) => {
  if (!item) return false;
  const source = getScheduledDiscountDraftSource(item);
  return source.discount_type !== form.discount_type
    || normalizeNullableNumber(source.discount_value) !== normalizeNullableNumber(form.discount_value);
};

// Dropdown компонент
function Dropdown({ value, options, onChange, placeholder, error, disabled = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('down');
  const ref = useRef();

  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find(opt => opt.value === value);

  const handleToggle = () => {
    if (disabled) return;
    haptic(open ? 'close' : 'open');
    if (!open) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = Math.min(options.length * 40, 200);
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDirection('up');
      } else {
        setDirection('down');
      }
    }
    setOpen(v => !v);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full px-3 py-2.5 border rounded-xl flex items-center justify-between focus:outline-none focus:ring-2 transition-colors text-sm ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed'
            : error
            ? 'bg-white dark:bg-gray-900 border-red-300 focus:ring-red-500'
            : value
            ? 'bg-white dark:bg-gray-900 border-blue-300 focus:ring-blue-500'
            : 'bg-white dark:bg-gray-900 border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
        }`}
      >
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-30 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-56 overflow-y-auto ${
              direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {options.map(opt => (
              <motion.button
                key={opt.value}
                type="button"
                onClick={() => {
                  haptic('selection');
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center justify-between ${
                  value === opt.value ? 'font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10' : 'text-gray-900 dark:text-gray-100'
                }`}
                whileHover={{ backgroundColor: '#f9fafb' }}
                whileTap={{ scale: 0.98 }}
              >
                {opt.label}
                {value === opt.value && <Check className="w-4 h-4 text-blue-500" />}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      {error && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function ManagementSegmentedControl({ value, options, onChange, columns, compact = false }) {
  return (
    <div
      className="grid gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800"
      style={{ gridTemplateColumns: `repeat(${columns || options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              haptic(active ? 'tap' : 'selection');
              onChange(option.value);
            }}
            className={`flex items-center justify-center rounded-lg font-medium transition-all ${
              compact ? 'px-2.5 py-2 text-[12px]' : 'px-4 py-2.5 text-[13px]'
            } ${
              active
                ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
            }`}
          >
            {Icon ? <Icon className={compact ? 'mr-1.5 h-4 w-4' : 'mr-2 h-4 w-4'} /> : null}
            <span className="whitespace-nowrap">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function DiscountsListSkeleton() {
  return (
    <div className="space-y-2 animate-pulse animate-slide-up">
      <div className="hidden items-center justify-between rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800 sm:flex">
        <div className="h-4 w-44 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800 sm:hidden">
        <div className="h-4 w-28 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700" />
      </div>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={`discount-skeleton-${index}`} className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-48 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-64 max-w-full rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex flex-wrap gap-2">
                <div className="h-5 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-5 w-16 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const DiscountsManagement = () => {
  // Состояния
  const [activeTab, setActiveTab] = useState('scheduled'); // 'scheduled' | 'promotions'
  const [clientType, setClientType] = useState('b2c'); // 'b2c' | 'b2b'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Данные
  const [scheduledDiscounts, setScheduledDiscounts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);

  // Фильтры
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Модальные окна
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [scheduledFormStep, setScheduledFormStep] = useState('main');
  const [editingItem, setEditingItem] = useState(null);

  const closeDiscountModal = useModalBackButton(showDiscountModal, () => {
    setShowDiscountModal(false);
    setScheduledFormStep('main');
    setEditingItem(null);
  });

  // Форма постоянной скидки
  const [scheduledForm, setScheduledForm] = useState({
    name: '',
    description: '',
    client_type: 'b2c',
    discount_type: 'percent',  // 'percent' | 'fixed'
    discount_value: 0,
    effective_from: new Date().toISOString().split('T')[0],
    system_code: null,
    auto_apply_enabled: false,
    min_items_count: null,
    allowed_roles: 'all',  // 'all' | 'dispatcher' | 'admin'
    active: true,
    planned_change: null,
    is_editing_existing: false
  });

  // Форма акции
  const [promotionForm, setPromotionForm] = useState({
    name: '',
    description: '',
    client_type: 'b2c',
    discount_type: 'percent',
    discount_value: 0,
    date_from: '',
    date_to: '',
    status: 'draft',
    min_order_amount: null,
    min_items_count: null,
    every_nth_item_free: null,
    scope: 'order',
    service_id: null,
    group_id: null,
    service_type: '',
    days_of_week: [],
    time_from: '',
    time_to: '',
    banner_color: '#3b82f6',
    priority: 0,
    combinable: false
  });

  // Модальные окна подтверждений и алертов
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
    onConfirm: null
  });
  const { showToast } = useToast();

  const [showDiscountHistoryModal, setShowDiscountHistoryModal] = useState(false);
  const [selectedDiscountForHistory, setSelectedDiscountForHistory] = useState(null);
  const [historyTargetType, setHistoryTargetType] = useState('discount_template');

  const closeDiscountHistoryModal = useModalBackButton(showDiscountHistoryModal, () => {
    setShowDiscountHistoryModal(false);
    setSelectedDiscountForHistory(null);
    setHistoryTargetType('discount_template');
  });

  // Загрузка данных
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        scheduled,
        promotionsData,
        groupsData,
        servicesData
      ] = await Promise.all([
        discountsApi.getScheduledDiscounts({ client_type: clientType, active_only: !showInactive }),
        discountsApi.getPromotions({ client_type: clientType }),
        servicesApi.getServiceGroups({ client_type: clientType, active_only: true }),
        servicesApi.getServices({ client_type: clientType, active_only: true })
      ]);
      setScheduledDiscounts(scheduled);
      setPromotions(promotionsData);
      setGroups(groupsData);
      setServices(servicesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientType, showInactive]);

  // Фильтрация
  const filteredScheduled = scheduledDiscounts.filter(item => {
    return item.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredPromotions = promotions.filter(item => {
    return item.name.toLowerCase().includes(searchQuery.toLowerCase())
      && (showInactive || !isPromotionArchivedLike(item));
  });

  const showConfirmModal = (title, message, onConfirm, type = 'danger', confirmText = 'Подтвердить') => {
    haptic(type === 'danger' ? 'warning' : type === 'warning' ? 'warning' : 'info');
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      confirmText,
      cancelText: 'Отмена',
      onConfirm
    });
  };

  const showAlertModal = (title, message, type = 'info') => {
    showToast(message || title, type);
  };

  const handleOpenScheduledModal = (item = null) => {
    haptic('open');
    setScheduledFormStep('main');
    if (item) {
      const source = getScheduledDiscountDraftSource(item);
      setEditingItem(item);
      setScheduledForm({
        name: item.name,
        description: item.description || '',
        client_type: item.client_type,
        discount_type: source.discount_type,
        discount_value: source.discount_value,
        effective_from: source.effective_from,
        system_code: item.system_code || null,
        auto_apply_enabled: !!item.auto_apply_enabled,
        min_items_count: source.min_items_count,
        allowed_roles: item.allowed_roles || 'all',
        active: item.active,
        planned_change: source.plannedChange,
        is_editing_existing: true
      });
    } else {
      setEditingItem(null);
      setScheduledForm({
        name: '',
        description: '',
        client_type: clientType,
        discount_type: 'percent',
        discount_value: 0,
        effective_from: new Date().toISOString().split('T')[0],
        system_code: null,
        auto_apply_enabled: false,
        min_items_count: null,
        allowed_roles: 'all',
        active: true,
        planned_change: null,
        is_editing_existing: false
      });
    }
    setShowDiscountModal(true);
  };

  const handleOpenPromotionModal = (item = null) => {
    haptic('open');
    if (item) {
      setEditingItem(item);
      setPromotionForm({
        name: item.name,
        description: item.description || '',
        client_type: item.client_type,
        discount_type: item.discount_type,
        discount_value: item.discount_value,
        date_from: normalizeDateFieldValue(item.date_from),
        date_to: normalizeDateFieldValue(item.date_to),
        status: item.status,
        min_order_amount: item.min_order_amount,
        min_items_count: item.min_items_count,
        every_nth_item_free: item.every_nth_item_free,
        scope: item.scope,
        service_id: item.service_id,
        group_id: item.group_id,
        service_type: item.service_type || '',
        days_of_week: Array.isArray(item.days_of_week) ? item.days_of_week : [],
        time_from: item.time_from || '',
        time_to: item.time_to || '',
        banner_color: item.banner_color || '#3b82f6',
        priority: item.priority || 0,
        combinable: item.combinable
      });
    } else {
      setEditingItem(null);
      setPromotionForm({
        name: '',
        description: '',
        client_type: clientType,
        discount_type: 'percent',
        discount_value: 10,
        date_from: new Date().toISOString().split('T')[0],
        date_to: '',
        status: 'active',
        min_order_amount: null,
        min_items_count: null,
        every_nth_item_free: null,
        scope: 'category',
        service_id: null,
        group_id: null,
        service_type: '',
        days_of_week: [],
        time_from: '',
        time_to: '',
        banner_color: '#3b82f6',
        priority: 0,
        combinable: false
      });
    }
    setShowDiscountModal(true);
  };

  const handleSaveScheduled = async (replaceScheduled = false) => {
    if (!scheduledForm.name || scheduledForm.name.trim() === '') {
      showAlertModal('Ошибка', 'Введите название скидки', 'error');
      return;
    }
    if (!scheduledForm.discount_value || parseFloat(scheduledForm.discount_value) <= 0) {
      showAlertModal('Ошибка', 'Введите размер скидки больше 0', 'error');
      return;
    }
    if (isDiscountPercentLimitExceeded(scheduledForm.discount_type, scheduledForm.discount_value)) {
      showAlertModal('Ошибка', 'Процентная скидка не может быть больше 100%', 'error');
      return;
    }
    if (!scheduledForm.effective_from) {
      showAlertModal('Ошибка', 'Выберите дату начала действия', 'error');
      return;
    }

    const requiresVersioning = editingItem ? hasScheduledDiscountVersioningChanges(scheduledForm, editingItem) : false;

    if (editingItem && requiresVersioning) {
      const selectedDate = new Date(`${scheduledForm.effective_from}T00:00:00`);
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (selectedDate < tomorrow) {
        showAlertModal('Ошибка', 'Изменение скидки можно запланировать только начиная с завтрашнего дня', 'error');
        return;
      }
    }

    const payload = {
      ...scheduledForm,
      replace_scheduled: replaceScheduled
    };
    delete payload.planned_change;
    delete payload.is_editing_existing;

    try {
      if (editingItem) {
        await discountsApi.updateScheduledDiscount(editingItem.id, payload);
        showAlertModal('Успешно', 'Скидка обновлена', 'success');
      } else {
        showConfirmModal(
          'Создание скидки',
          `Создать скидку "${scheduledForm.name}" — ${scheduledForm.discount_value}${scheduledForm.discount_type === 'percent' ? '%' : '₽'}?`,
          async () => {
            try {
              await discountsApi.createScheduledDiscount(payload);
              closeDiscountModal();
              loadData();
              showAlertModal('Успешно', 'Скидка создана', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'info',
          'Создать'
        );
        return;
      }
      closeDiscountModal();
      loadData();
    } catch (err) {
      if (err?.detail?.code === 'scheduled_discount_exists') {
        const scheduled = err.detail.scheduled_change;
        const scheduledDate = scheduled?.effective_from
          ? new Date(scheduled.effective_from).toLocaleDateString('ru-RU')
          : 'выбранную дату';
        const scheduledValue = scheduled
          ? `${scheduled.discount_value}${scheduled.discount_type === 'percent' ? '%' : ' ₽'}`
          : 'запланированное изменение';
        showConfirmModal(
          'Запланированное изменение уже есть',
          `Сейчас уже запланировано ${scheduledValue} с ${scheduledDate}. Перезаписать это изменение новым значением?`,
          async () => {
            await handleSaveScheduled(true);
          },
          'warning',
          'Перезаписать'
        );
        return;
      }
      showAlertModal('Ошибка', err.message, 'error');
    }
  };

  const handleSavePromotion = async () => {
    if (!promotionForm.name || promotionForm.name.trim() === '') {
      showAlertModal('Ошибка', 'Введите название акции', 'error');
      return;
    }
    if (!promotionForm.discount_value || parseFloat(promotionForm.discount_value) <= 0) {
      showAlertModal('Ошибка', 'Введите размер скидки больше 0', 'error');
      return;
    }
    if (isDiscountPercentLimitExceeded(promotionForm.discount_type, promotionForm.discount_value)) {
      showAlertModal('Ошибка', 'Процентная скидка не может быть больше 100%', 'error');
      return;
    }

    if (!promotionForm.date_from) {
      showAlertModal('Ошибка', 'Выберите дату начала действия', 'error');
      return;
    }
    if (promotionForm.date_to && promotionForm.date_to < promotionForm.date_from) {
      showAlertModal('Ошибка', 'Дата окончания не может быть раньше даты начала', 'error');
      return;
    }
    if ((promotionForm.time_from && !promotionForm.time_to) || (!promotionForm.time_from && promotionForm.time_to)) {
      showAlertModal('Ошибка', 'Для временного окна укажите и начало, и конец', 'error');
      return;
    }
    if (!PROMOTION_SCOPE_OPTIONS.some(option => option.value === promotionForm.scope)) {
      showAlertModal('Ошибка', 'Акция должна быть привязана к типу услуги, группе или конкретной услуге', 'error');
      return;
    }
    if (!promotionForm.service_type) {
      showAlertModal('Ошибка', 'Выберите тип услуги для акции', 'error');
      return;
    }
    if (promotionForm.scope === 'group' && !promotionForm.group_id) {
      showAlertModal('Ошибка', 'Выберите группу услуг', 'error');
      return;
    }
    if (promotionForm.scope === 'service' && !promotionForm.service_id) {
      showAlertModal('Ошибка', 'Выберите услугу', 'error');
      return;
    }

    const payload = normalizePromotionPayload(promotionForm);

    try {
      if (editingItem) {
        await discountsApi.updatePromotion(editingItem.id, payload);
        showAlertModal('Успешно', 'Акция обновлена', 'success');
      } else {
        showConfirmModal(
          'Создание акции',
          `Вы уверены, что хотите создать акцию "${promotionForm.name}"?`,
          async () => {
            try {
              await discountsApi.createPromotion(payload);
              closeDiscountModal();
              loadData();
              showAlertModal('Успешно', 'Акция создана', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'info',
          'Создать'
        );
        return;
      }
      closeDiscountModal();
      loadData();
    } catch (err) {
      showAlertModal('Ошибка', err.message, 'error');
    }
  };

  const handleDeleteScheduled = async (id) => {
    showConfirmModal(
      'Удаление скидки',
      'Вы уверены, что хотите удалить скидку?',
      async () => {
        try {
          await discountsApi.deleteScheduledDiscount(id);
          loadData();
          showAlertModal('Успешно', 'Скидка удалена', 'success');
        } catch (err) {
          showAlertModal('Ошибка', err.message, 'error');
        }
      },
      'danger',
      'Удалить'
    );
  };

  const handleDeletePromotion = async (id) => {
    showConfirmModal(
      'Удаление акции',
      'Вы уверены, что хотите удалить акцию?',
      async () => {
        try {
          await discountsApi.deletePromotion(id);
          loadData();
          showAlertModal('Успешно', 'Акция удалена', 'success');
        } catch (err) {
          showAlertModal('Ошибка', err.message, 'error');
        }
      },
      'danger',
      'Удалить'
    );
  };

  const handleCloseModal = () => {
    haptic('close');
    closeDiscountModal();
    setScheduledFormStep('main');
  };

  const handleOpenDiscountHistory = (item) => {
    haptic('open');
    setHistoryTargetType('discount_template');
    setSelectedDiscountForHistory(item);
    setShowDiscountHistoryModal(true);
  };

  const handleOpenPromotionHistory = (item) => {
    haptic('open');
    setHistoryTargetType('promotion');
    setSelectedDiscountForHistory(item);
    setShowDiscountHistoryModal(true);
  };

  // Получение групп для формы
  const getFormGroups = () => {
    return groups;
  };

  // Получение услуг для формы
  const getFormServices = () => {
    return services;
  };

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      {/* Переключатель B2C/B2B */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Тип клиента</span>
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
        </div>
        <ManagementSegmentedControl
          value={clientType}
          onChange={setClientType}
          compact
          options={[
            { value: 'b2c', label: 'Физ. лица', icon: Users },
            { value: 'b2b', label: 'Организации', icon: BriefcaseBusiness }
          ]}
        />
      </div>

      {/* Вкладки */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up">
        <ManagementSegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          compact
          options={[
            { value: 'scheduled', label: 'Основные', icon: TrendingUp },
            { value: 'promotions', label: 'Акции', icon: Gift }
          ]}
        />
      </div>

      {/* Панель инструментов */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up">
        <div className="flex flex-wrap items-center gap-2">
          {/* Поиск */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>

          {/* Показать неактивные */}
          <button
            onClick={() => {
              haptic('selection');
              setShowInactive(!showInactive);
            }}
            className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center text-sm ${
              showInactive
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            {showInactive ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
            Неактивные
          </button>

          {activeTab === 'scheduled' && (
            <button
              onClick={() => handleOpenScheduledModal()}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium transition-colors hover:bg-blue-700 shadow-sm flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Добавить
            </button>
          )}

          {activeTab === 'promotions' && (
            <button
              onClick={() => handleOpenPromotionModal()}
              className="px-4 py-2 bg-pink-600 text-white rounded-xl font-medium transition-colors hover:bg-pink-700 shadow-sm flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Новая акция
            </button>
          )}
        </div>
      </div>

      {/* Список */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm animate-slide-up min-h-[400px]">
        {loading ? (
          <div className="p-3 sm:p-4">
            <DiscountsListSkeleton />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : activeTab === 'scheduled' ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredScheduled.length === 0 && (
              <div className="text-center py-20 text-gray-500 dark:text-gray-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Нет постоянных скидок</p>
              </div>
            )}
            {filteredScheduled.map(item => (
              <ScheduledDiscountItem
                key={item.id}
                item={item}
                onEdit={() => handleOpenScheduledModal(item)}
                onDelete={() => handleDeleteScheduled(item.id)}
                onHistory={() => handleOpenDiscountHistory(item)}
              />
            ))}
          </div>
        ) : activeTab === 'promotions' ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredPromotions.length === 0 && (
              <div className="text-center py-20 text-gray-500 dark:text-gray-400">
                <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Нет акций</p>
              </div>
            )}
            {filteredPromotions.map(item => (
              <PromotionItem
                key={item.id}
                item={item}
                onHistory={() => handleOpenPromotionHistory(item)}
                onEdit={() => handleOpenPromotionModal(item)}
                onDelete={() => handleDeletePromotion(item.id)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500 dark:text-gray-400">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Раздел не найден</p>
          </div>
        )}
      </div>

      {/* Модальное окно редактирования/создания */}
      <ModalShell
        open={showDiscountModal}
        onClose={handleCloseModal}
        variant="sheet"
        zIndexClass="z-[70]"
        panelClassName="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        closeOnOverlay={false}
      >
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  {activeTab === 'scheduled' && scheduledFormStep === 'pricing' && (
                    <button
                      onClick={() => {
                        haptic('close');
                        setScheduledFormStep('main');
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-500" />
                    </button>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {activeTab === 'scheduled' && scheduledFormStep === 'pricing'
                      ? 'Размер и дата скидки'
                      : `${editingItem ? 'Редактирование' : 'Создание'}${activeTab === 'scheduled' ? ' постоянной скидки' : ' акции'}`}
                  </h3>
                </div>
                <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-3.5 space-y-3">
                {activeTab === 'scheduled' && (
                  <ScheduledDiscountForm
                    form={scheduledForm}
                    setForm={setScheduledForm}
                    step={scheduledFormStep}
                    onStepChange={setScheduledFormStep}
                  />
                )}
                {activeTab === 'promotions' && (
                  <PromotionForm
                    form={promotionForm}
                    setForm={setPromotionForm}
                    groups={getFormGroups()}
                    services={getFormServices()}
                  />
                )}
              </div>

              <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4 flex gap-2">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={() => {
                    if (activeTab === 'scheduled') handleSaveScheduled();
                    else handleSavePromotion();
                  }}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium transition-colors hover:bg-blue-700 shadow-sm"
                >
                  Сохранить
                </button>
              </div>
      </ModalShell>

      {/* Модальные окна подтверждений */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={() => {
          haptic('success');
          confirmModal.onConfirm?.();
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null });
        }}
        onCancel={() => {
          haptic('close');
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null });
        }}
      />


      <DiscountActivityModal
        open={showDiscountHistoryModal}
        onClose={() => {
          haptic('close');
          closeDiscountHistoryModal();
        }}
        subject={selectedDiscountForHistory}
        subjectType={historyTargetType}
      />
    </div>
  );
};

// Компонент элемента запланированной скидки
// Компонент элемента постоянной скидки
function ScheduledDiscountItem({ item, onEdit, onDelete, onHistory }) {
  const rolesLabel = {
    all: 'Все',
    dispatcher: 'Диспетчер+',
    admin: 'Только админ'
  };
  const shouldShowDescription = item.description && !item.description.toLowerCase().startsWith('системная скидка');

  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{item.name}</h3>
            {item.system_code && (
              <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 text-xs rounded-full">
                системная
              </span>
            )}
            {item.auto_apply_enabled && (
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-200 text-xs rounded-full">
                авто
              </span>
            )}
            {!item.active && (
              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                Неактивна
              </span>
            )}
          </div>
          {shouldShowDescription && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{item.description}</p>
          )}
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-purple-600 dark:text-purple-400">
              {item.discount_value}{item.discount_type === 'percent' ? '%' : ' ₽'}
            </span>
            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full">
              {rolesLabel[item.allowed_roles] || 'Все'}
            </span>
            {item.system_code === 'wholesale_order' && item.min_items_count && (
              <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                от {item.min_items_count} предм.
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={onHistory}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
            title="История"
          >
            <History className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </button>
          <button onClick={onEdit} className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
            <Edit2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </button>
          {!item.system_code && (
            <button onClick={onDelete} className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors">
              <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Компонент элемента акции
function PromotionItem({ item, onHistory, onEdit, onDelete }) {
  const displayStatus = getPromotionDisplayStatus(item);
  const formatDate = (dateStr) => {
    if (!dateStr) return 'бессрочно';
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{item.name}</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              displayStatus === 'active'
                ? 'bg-green-200 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : displayStatus === 'draft' || displayStatus === 'archived'
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  : displayStatus === 'expired'
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'bg-yellow-200 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
            }`}>
              {PROMOTION_STATUS_LABELS[displayStatus] || PROMOTION_STATUS_LABELS[item.status]}
            </span>
          </div>
          
          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4" />
              <span>{item.discount_value}{item.discount_type === 'percent' ? '%' : ' ₽'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(item.date_from)} — {formatDate(item.date_to)}</span>
            </div>
            {item.min_order_amount && (
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span>от {item.min_order_amount} ₽</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onHistory}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
            title="История"
          >
            <History className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </button>
          <button
            onClick={onEdit}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Форма запланированной скидки
// Форма постоянной скидки
function ScheduledDiscountForm({ form, setForm, step = 'main', onStepChange }) {
  const isSystemDiscount = !!form.system_code;
  const isWholesaleDiscount = form.system_code === 'wholesale_order';
  const systemConditionText = form.system_code === 'no_delivery'
    ? 'Авто-включение сработает только когда в заказе нет доставки от клиента и доставки до клиента.'
    : 'Авто-включение сработает, когда количество предметов достигнет выбранного минимума.';
  const discountPreview = `${form.discount_value || 0}${form.discount_type === 'percent' ? '%' : ' ₽'}`;
  const effectiveFromLabel = form.effective_from
    ? new Date(`${form.effective_from}T00:00:00`).toLocaleDateString('ru-RU')
    : 'сегодня';
  const plannedChange = form.planned_change || null;
  const plannedChangeLabel = plannedChange?.effective_from
    ? new Date(plannedChange.effective_from).toLocaleDateString('ru-RU')
    : '';
  const plannedValueLabel = plannedChange
    ? `${plannedChange.discount_value}${plannedChange.discount_type === 'percent' ? '%' : ' ₽'}`
    : '';
  const minEffectiveDate = form.is_editing_existing
    ? new Date(Date.now() + 86400000).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-3">
      {step === 'main' ? (
        <>
      {/* Название */}
      <div>
        <FloatingField label="Название *" disabled={isSystemDiscount}>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={isSystemDiscount}
            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500 dark:text-gray-100 dark:disabled:bg-gray-800"
            placeholder="Например: Скидка постоянному клиенту"
          />
        </FloatingField>
      </div>

      {/* Описание */}
      <div>
        <FloatingField label="Описание">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-gray-100"
            rows={2}
            placeholder="Описание скидки"
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        </FloatingField>
        <div className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
          {(form.description || '').length}/{DESCRIPTION_MAX_LENGTH}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          haptic('open');
          onStepChange?.('pricing');
        }}
        className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/70 dark:hover:bg-gray-800"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <CalendarRange className="h-4 w-4 text-purple-500" />
              <span>Размер и дата</span>
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {discountPreview} · действует с {effectiveFromLabel}
            </div>
          </div>
          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-purple-600 shadow-sm dark:bg-gray-900 dark:text-purple-300">
            Изменить
          </span>
        </div>
      </button>

      {plannedChange && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900/60 dark:bg-amber-950/20">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
            <Clock className="h-4 w-4" />
            <span>Запланировано изменение</span>
          </div>
          <div className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
            {plannedValueLabel} с {plannedChangeLabel}
          </div>
        </div>
      )}

      {isSystemDiscount && (
        <div className="space-y-3 rounded-xl border-2 border-purple-200 bg-purple-50/70 p-3 dark:border-purple-900/70 dark:bg-purple-950/20">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                системная
              </span>
              <span className="text-sm font-semibold text-purple-800 dark:text-purple-200">Автонастройки</span>
            </div>
            <p className="mt-1 text-xs leading-4 text-purple-700 dark:text-purple-200">
              {systemConditionText}
            </p>
          </div>
          <label className="flex items-center gap-2 rounded-xl bg-white p-2 dark:bg-gray-900">
            <input
              type="checkbox"
              checked={!!form.auto_apply_enabled}
              onChange={(e) => {
                haptic('selection');
                setForm({ ...form, auto_apply_enabled: e.target.checked });
              }}
              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Автоматически применять</span>
          </label>
          {isWholesaleDiscount && (
            <div>
              <FloatingField label="Минимум предметов для оптового заказа">
                <input
                  type="number"
                  min="1"
                  value={form.min_items_count || ''}
                  onChange={(e) => setForm({ ...form, min_items_count: parseInt(e.target.value, 10) || null })}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-gray-100"
                  placeholder="Например: 10"
                />
              </FloatingField>
            </div>
          )}
        </div>
      )}

      {/* Кто может использовать */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Кто может применять</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'all', label: 'Все' },
            { value: 'dispatcher', label: 'Диспетчер+' },
            { value: 'admin', label: 'Только админ' }
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                haptic(form.allowed_roles === opt.value ? 'tap' : 'selection');
                setForm({ ...form, allowed_roles: opt.value });
              }}
              className={`rounded-xl py-1.5 text-sm font-medium transition-all ${form.allowed_roles === opt.value ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {form.allowed_roles === 'all' && 'Могут применять все пользователи'}
          {form.allowed_roles === 'dispatcher' && 'Могут применять диспетчеры и выше'}
          {form.allowed_roles === 'admin' && 'Могут применять только администраторы'}
        </p>
      </div>

      {/* Активна */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Активна</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {form.active ? 'Скидка доступна для применения' : 'Скидка выключена'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('selection');
            setForm({ ...form, active: !form.active });
          }}
          className="toggle-switch shrink-0"
          style={{
            '--toggle-bg': form.active ? 'rgb(168 85 247)' : 'rgb(209 213 219)',
            '--toggle-height': '22px',
            '--toggle-width': '42px',
            '--knob-x': form.active ? '20px' : '2px'
          }}
          aria-pressed={form.active}
          aria-label="Активна"
        >
          <span className="toggle-switch-knob" />
        </button>
      </div>
        </>
      ) : (
        <>
          {plannedChange && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900/60 dark:bg-amber-950/20">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
                <Clock className="h-4 w-4" />
                <span>Сейчас запланировано</span>
              </div>
              <div className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
                {plannedValueLabel} с {plannedChangeLabel}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-purple-200 bg-purple-50/60 p-4 dark:border-purple-900/60 dark:bg-purple-950/20">
            <div className="text-sm font-semibold text-purple-800 dark:text-purple-200">Размер скидки и дата начала</div>
            <p className="mt-1 text-xs leading-5 text-purple-700 dark:text-purple-300">
              Новое значение будет применяться только к заказам, созданным после выбранной даты.
            </p>
          </div>

          <div>
            <div className="flex gap-2">
              <FloatingField label="Размер скидки *" className="min-w-0 flex-1">
                <input
                  type="number"
                  value={form.discount_value ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    discount_value: e.target.value === '' ? '' : clampDiscountValue(e.target.value, form.discount_type)
                  })}
                  onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  min="0"
                  max={getDiscountValueInputMax(form.discount_type)}
                  step="0.01"
                  placeholder="0"
                />
              </FloatingField>
              <div className="flex rounded-xl overflow-hidden border border-gray-300 dark:border-gray-700 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    haptic(form.discount_type === 'percent' ? 'tap' : 'selection');
                    setForm({
                      ...form,
                      discount_type: 'percent',
                      discount_value: clampDiscountValue(form.discount_value, 'percent')
                    });
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold transition-all ${form.discount_type === 'percent' ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
                >%</button>
                <button
                  type="button"
                  onClick={() => {
                    haptic(form.discount_type === 'fixed' ? 'tap' : 'selection');
                    setForm({ ...form, discount_type: 'fixed' });
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold transition-all border-l border-gray-300 dark:border-gray-700 ${form.discount_type === 'fixed' ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
                >₽</button>
              </div>
            </div>
          </div>

          <div>
            <DatePicker
              value={form.effective_from}
              onChange={(date) => setForm({ ...form, effective_from: date })}
              placeholder="Дата начала действия"
              showTodayButton={false}
              minDate={minEffectiveDate}
              inputClassName={MANAGEMENT_DATE_PICKER_INPUT_CLASS}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Форма акции
function PromotionForm({ form, setForm, groups, services }) {
  const filteredGroups = (groups || []).filter((group) => group.service_type === form.service_type);
  const filteredServices = (services || []).filter((service) => service.service_type === form.service_type);
  const clientTypeOptions = [
    { value: 'b2c', label: 'Физ. лица' },
    { value: 'b2b', label: 'Компании' }
  ];
  const toggleWeekday = (day) => {
    const currentDays = Array.isArray(form.days_of_week) ? form.days_of_week : [];
    const nextDays = currentDays.includes(day)
      ? currentDays.filter((entry) => entry !== day)
      : [...currentDays, day].sort((a, b) => a - b);
    setForm({ ...form, days_of_week: nextDays });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-pink-200 bg-pink-50/70 p-4 dark:border-pink-900/60 dark:bg-pink-950/20">
        <div className="text-sm font-semibold text-pink-800 dark:text-pink-200">Акция меняет базовую цену услуги</div>
        <p className="mt-1 text-xs leading-5 text-pink-700 dark:text-pink-300">
          Сначала считается акционная база услуги, а уже потом от неё рассчитывается коэффициент подразделения.
        </p>
      </div>

      <div>
        <FloatingField label="Название *">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
            placeholder="Например: Утренние цены на пледы"
          />
        </FloatingField>
      </div>

      <div>
        <FloatingField label="Описание">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full resize-none px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
            rows={2}
            placeholder="Описание акции"
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        </FloatingField>
        <div className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
          {(form.description || '').length}/{DESCRIPTION_MAX_LENGTH}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Dropdown
            label="Для кого"
            value={form.client_type}
            options={clientTypeOptions}
            onChange={(value) => setForm({ ...form, client_type: value })}
          />
        </div>

        <div>
          <Dropdown
            label="Статус"
            value={form.status}
            options={Object.entries(PROMOTION_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            onChange={(value) => setForm({ ...form, status: value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Dropdown
            label="Тип скидки"
            value={form.discount_type}
            options={[
              { value: 'percent', label: 'Процентная (%)' },
              { value: 'fixed', label: 'Фиксированная (₽)' }
            ]}
            onChange={(value) => setForm({
              ...form,
              discount_type: value,
              discount_value: clampDiscountValue(form.discount_value, value)
            })}
          />
        </div>

        <div>
          <FloatingField label="Размер *">
            <input
              type="number"
              value={form.discount_value ?? ''}
              onChange={(e) => setForm({
                ...form,
                discount_value: e.target.value === '' ? '' : clampDiscountValue(e.target.value, form.discount_type)
              })}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              min="0"
              max={getDiscountValueInputMax(form.discount_type)}
              step="0.01"
            />
          </FloatingField>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Даты проведения *
        </label>
        <div className="grid grid-cols-2 gap-3">
          <DatePicker
            value={form.date_from}
            onChange={(date) => setForm({ ...form, date_from: date })}
            placeholder="Дата начала"
            allowPastDates={true}
            inputClassName={MANAGEMENT_DATE_PICKER_INPUT_CLASS}
          />
          <DatePicker
            value={form.date_to}
            onChange={(date) => setForm({ ...form, date_to: date })}
            placeholder="Дата окончания"
            allowPastDates={true}
            inputClassName={MANAGEMENT_DATE_PICKER_INPUT_CLASS}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Время действия
          </label>
          <div className="grid grid-cols-2 gap-3">
            <FloatingField label="С">
              <input
                type="time"
                value={form.time_from || ''}
                onChange={(e) => setForm({ ...form, time_from: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </FloatingField>
            <FloatingField label="По">
              <input
                type="time"
                value={form.time_to || ''}
                onChange={(e) => setForm({ ...form, time_to: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </FloatingField>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Оставьте пустым, если акция действует весь день.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Дни недели
          </label>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-4">
            {WEEKDAY_OPTIONS.map((option) => {
              const active = Array.isArray(form.days_of_week) && form.days_of_week.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleWeekday(option.value)}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-pink-500 bg-pink-500 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-pink-300 hover:text-pink-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Если ничего не выбрано, акция действует каждый день.
          </p>
        </div>
      </div>

      <div>
        <Dropdown
          label="Область применения"
          value={form.scope}
          options={PROMOTION_SCOPE_OPTIONS}
          onChange={(value) => setForm({
            ...form,
            scope: value,
            group_id: value === 'group' ? form.group_id : null,
            service_id: value === 'service' ? form.service_id : null
          })}
        />
      </div>

      <div>
        <Dropdown
          label="Тип услуги"
          value={form.service_type || ''}
          options={SERVICE_TYPE_OPTIONS}
          onChange={(value) => setForm({
            ...form,
            service_type: value,
            service_id: null,
            group_id: null
          })}
          placeholder="Выберите тип услуги"
        />
      </div>

      {form.scope === 'group' && form.service_type && (
        <div>
          <Dropdown
            label="Группа услуг"
            value={form.group_id || ''}
            options={filteredGroups.map((group) => ({ value: group.id, label: group.name }))}
            onChange={(value) => setForm({ ...form, group_id: value || null })}
            placeholder="Выберите группу"
          />
        </div>
      )}

      {form.scope === 'service' && form.service_type && (
        <div>
          <Dropdown
            label="Услуга"
            value={form.service_id || ''}
            options={filteredServices.map((service) => {
              const group = filteredGroups.find((entry) => entry.id === service.group_id);
              return {
                value: service.id,
                label: group ? `${group.name} / ${service.name}` : service.name
              };
            })}
            onChange={(value) => setForm({ ...form, service_id: value || null })}
            placeholder="Выберите услугу"
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <FloatingField label="Мин. сумма заказа">
            <input
            type="number"
            value={form.min_order_amount ?? ''}
            onChange={(e) => setForm({ ...form, min_order_amount: e.target.value === '' ? null : Number(e.target.value) })}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
            min="0"
            step="0.01"
            placeholder="Не ограничено"
            />
          </FloatingField>
        </div>

        <div>
          <FloatingField label="Мин. предметов">
            <input
            type="number"
            value={form.min_items_count ?? ''}
            onChange={(e) => setForm({ ...form, min_items_count: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
            min="1"
            step="1"
            placeholder="Не ограничено"
            />
          </FloatingField>
        </div>

        <div>
          <FloatingField label="Приоритет">
            <input
            type="number"
            value={form.priority ?? 0}
            onChange={(e) => setForm({ ...form, priority: e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0 })}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
            step="1"
            />
          </FloatingField>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            При пересечении выше приоритет сработает раньше.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Цвет баннера
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={form.banner_color}
            onChange={(e) => setForm({ ...form, banner_color: e.target.value })}
            className="w-12 h-10 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">{form.banner_color}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="combinable"
          checked={form.combinable}
          onChange={(e) => setForm({ ...form, combinable: e.target.checked })}
          className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
        />
        <label htmlFor="combinable" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Можно комбинировать с другими скидками
        </label>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Акция меняет базовую цену каталога. Дополнительные скидки поверх неё лучше включать только в исключительных случаях.
      </p>
    </div>
  );
}

export default DiscountsManagement;
